import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../U3500';
import {
  doGetProjectDocumentTree,
  luHandleAppiError,
  type U3500DocumentTreeResponse,
} from '../U3500Service';

export interface UseU3500ProjectDocumentTreeHandlersParams {
  onSuccess?: (tree: U3500DocumentTreeResponse) => void;
  onError?: (error: unknown) => void;
}

/**
 * Handler : charge l’arbre documents d’un projet (GET U3500/projects/:id/documents/tree).
 */
export function useU3500ProjectDocumentTreeHandlers(
  params: UseU3500ProjectDocumentTreeHandlersParams = {}
) {
  const { onSuccess, onError } = params;
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const loadTree = useCallback(
    async (projectId: string): Promise<U3500DocumentTreeResponse | null> => {
      if (!projectId?.trim()) {
        return null;
      }
      try {
        const tree = await doGetProjectDocumentTree(axios, projectId.trim());
        onSuccess?.(tree);
        return tree;
      } catch (error: unknown) {
        const err = U2000HandleApiError(
          error as Parameters<typeof U2000HandleApiError>[0],
          t,
          NS,
          luHandleAppiError
        );
        if (err.genericErrors?.length) {
          err.genericErrors.forEach((e) => toast.error(e.text, e.title));
        }
        onError?.(error);
        return null;
      }
    },
    [axios, onSuccess, onError, t, toast]
  );

  return { loadTree };
}
